import { resolveVerticalDashboardConfig } from "./verticalDashboardConfig";

function buildSwipeRouteOrder(sectionGroups) {
  const seen = new Set();
  return sectionGroups.flatMap((group) =>
    group.filter((path) => {
      if (!path || seen.has(path)) return false;
      seen.add(path);
      return true;
    })
  );
}

// Source of truth: the live client app navigation surfaces.
// Ordered from the actual visible page flows users traverse:
// - Dashboard tile order
// - Social Analytics tile order
// - Programmatic Analytics tile order
// - Campaign Manager tile order
// - Nexus Hub tile order
// - Genesis Hub tile order
// - Profile Hub tile order
//
// Intentionally excluded from swipe navigation:
// - public/auth utility routes such as /login, /forgot-password, /set-password, /authorize/return
// - hidden/internal routes not exposed in the live client UI such as /sherloc-ai and /creative-optimus
const HOME_SEQUENCE = [
  "/dashboard",
  "/sherloc-reports",
];

const SOCIAL_ANALYTICS_ORDER = [
  "/social-analytics",
  "/facebook-analytics",
  "/instagram-analytics",
  "/tiktok-analytics",
  "/x-analytics",
  "/youtube-analytics",
  "/google-analytics",
  "/rumble-analytics",
  "/truthsocial-analytics",
  "/reddit-analytics",
  "/amazon-analytics",
  "/yahoo-analytics",
  "/shopify-analytics",
  "/linkedin-analytics",
  "/community-analytics",
];

const PROGRAMMATIC_ANALYTICS_ORDER = [
  "/programmatic-analytics",
  "/dooh-analytics",
  "/ctv-analytics",
  "/video-analytics",
  "/audio-analytics",
  "/display-analytics",
  "/mobile-analytics",
  "/ingame-analytics",
  "/email-analytics",
];

// Vertical-restricted intelligence pages. Exactly one of these is eligible for
// a given advertiser — the route VERTICAL_DASHBOARD_CONFIG assigns to their
// vertical (the same registry that drives the Dashboard vertical tile). The
// swipeRouteOrder.test.js drift guard pins this list to that registry, so a new
// vertical page must be added there first.
export const VERTICAL_INTELLIGENCE_ORDER = [
  "/cinema-analytics",
  "/auto-intelligence",
  "/retail-intelligence",
  "/travel-intelligence",
  "/general-intelligence",
];

const CAMPAIGN_MANAGER_ORDER = [
  "/campaign-management",
  "/ai-optimizations",
  "/optimus",
];

// Creative Studio's client-authorized surfaces ONLY. /creative-trafficking,
// /creative-executions and /creative-analysis are protected Beta surfaces
// (CREATIVE_BETA_INTERNAL_ACCESS) and were removed here: leaving them in this
// array is what let a client session swipe straight past the disabled hub
// tiles and onto the pages. The sequence must never contain a route the
// current principal cannot open — swipe is navigation, and navigation is
// subject to the same authorization as a click.
const CREATIVE_HUB_ORDER = [
  "/nexus-hub",
  "/creative-studio",
];

const GENESIS_HUB_ORDER = [
  "/genesis-hub",
  "/sherloc-media-planner",
  "/sherloc-media-campaigns",
  "/authorize",
  "/billing",
  "/integrations",
];

const PROFILE_HUB_ORDER = [
  "/profile-hub",
  "/settings",
  "/notifications",
  "/install-app",
];

function buildSectionGroups(verticalIntelligenceGroup) {
  return [
    HOME_SEQUENCE,
    SOCIAL_ANALYTICS_ORDER,
    PROGRAMMATIC_ANALYTICS_ORDER,
    verticalIntelligenceGroup,
    CAMPAIGN_MANAGER_ORDER,
    CREATIVE_HUB_ORDER,
    GENESIS_HUB_ORDER,
    PROFILE_HUB_ORDER,
  ];
}

// The complete canonical page order, with every vertical intelligence route
// present. This list is NOT advertiser-aware: gesture navigation must resolve
// its sequence through resolveGovernedSwipeRouteOrder so a route the current
// advertiser is not eligible for can never become a previous/next destination.
export const SWIPE_ROUTE_ORDER = buildSwipeRouteOrder(
  buildSectionGroups(VERTICAL_INTELLIGENCE_ORDER)
);

// Advertiser-aware gesture sequence: identical to SWIPE_ROUTE_ORDER except the
// vertical intelligence slot collapses to the single route the advertiser's
// vertical is eligible for. Unknown/empty verticals normalize to "general"
// inside resolveVerticalDashboardConfig, so this never throws and never
// returns a sequence containing another vertical's restricted page.
export function resolveGovernedSwipeRouteOrder(advertiserVertical) {
  const eligibleVerticalRoute =
    resolveVerticalDashboardConfig(advertiserVertical).route;
  return buildSwipeRouteOrder(buildSectionGroups([eligibleVerticalRoute]));
}
